import { isUploadActive, useUploadQueue } from '@hooks/useUploadQueue';
import { Progress } from '@components/ui/progress';
import { Button } from '@components/ui/button';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, WifiOff, X } from 'lucide-react';
import { useEffect } from 'react';

export const UploadQueue = () => {
  const { uploads } = useUploadQueue();
  const { t } = useTranslation('app');
  const hasActiveUploads = uploads.some(isUploadActive);

  useEffect(() => {
    if (!hasActiveUploads) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads]);

  if (!uploads.length) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 left-4 z-[90] flex w-full max-w-[320px] flex-col gap-2">
      {uploads.map(({ id, label, progress, status, errorKind, onRetry, onDismiss }) => (
        <div
          key={id}
          className="pointer-events-auto flex flex-col gap-2 rounded-md border border-neutral-100 bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between gap-2 text-sm text-neutral-700">
            <span className="truncate">{label}</span>
            {status === 'success' && <Check className="h-4 w-4 shrink-0 text-success-500" />}
            {status === 'error' && <X className="h-4 w-4 shrink-0 text-danger-500" />}
            {status === 'retrying' && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-neutral-500" />
            )}
            {status === 'offline' && <WifiOff className="h-4 w-4 shrink-0 text-neutral-500" />}
          </div>
          <Progress
            value={progress}
            bar={
              status === 'error'
                ? 'bg-danger-500'
                : status === 'retrying' || status === 'offline'
                  ? 'bg-neutral-400'
                  : 'bg-neutral-900'
            }
          />
          <span className="text-xs text-neutral-500">
            {status === 'uploading' && t('fileUpload.queue.uploading', { progress })}
            {status === 'retrying' && t('fileUpload.queue.retrying')}
            {status === 'offline' && t('fileUpload.queue.offline')}
            {status === 'success' && t('fileUpload.queue.success')}
            {status === 'error' &&
              t(
                errorKind === 'tooLarge'
                  ? 'fileUpload.queue.tooLarge'
                  : errorKind === 'network'
                    ? 'fileUpload.queue.networkError'
                    : 'fileUpload.queue.error',
              )}
          </span>
          {(onRetry || onDismiss) && (
            <div className="flex justify-end gap-2">
              {onDismiss && (
                <Button variant="ghost" size="sm" disabled={false} onClick={onDismiss}>
                  {t('fileUpload.queue.dismiss')}
                </Button>
              )}
              {onRetry && (
                <Button size="sm" disabled={false} onClick={onRetry}>
                  {t('fileUpload.queue.retry')}
                </Button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
